<template lang="pug">
.container
  custom-product-summary(
    summaryClass="cave-summary"
    :buyRequest="buyRequest"
    :childBuyRequest="buyRequest"
    :groupMeta="groupMeta"
    :badgeMeta="badgeMeta"
  )
  Accordion.accordian-styles(
    v-model="openAccordions[0]"
    heading="Rack Configuration"
    open-icon="arrow-up"
    closed-icon="arrow-down"
    :theme="'custom-rhpa-styles'"
    heading-tag="h3"
  )
    slot(name="rhpaBody")
      rhpa-body(:buyRequest="mainRack" :groupMeta="groupMeta")
  .side-container
    custom-side-selector(
      :sideOptions="sideOptions"
      :modelValue="sideSelection"
      @update:modelValue="handleSideSelectionChange"
    )
    custom-side-accordion(
      v-if="leftSideEnabled"
      v-model="openAccordions[1]"
      heading="Left Side"
      :buyRequest="leftSide"
      :groupMeta="groupMeta"
    )
      template(#rhpaBody)
        slot(name="rhpaBody")
  custom-side-accordion(
    v-if="rightSideEnabled"
    v-model="openAccordions[2]"
    heading="Right Side"
    :buyRequest="rightSide"
    :groupMeta="groupMeta"
  )
    template(#rhpaBody)
      slot(name="rhpaBody")
  .divider
</template>

<script lang="ts" setup>
import { ref, computed, watchEffect, provide, toRef } from 'vue'
import RhpaBody from './rhpa-body.vue'
import CustomProductSummary from './custom/custom-product-summary.vue'
import CustomSideSelector from './custom/custom-side-selector.vue'
import CustomSideAccordion from './custom/custom-side-accordion.vue'
import { useSideSelection } from './custom/useSideSelection'
import { getBuyRequestMeta, getSimpleForBuyRequest } from '@/js/helpers/buy-request'
import { useRogue } from '@/js/services/Rogue'
import type { BuyRequest } from '@/js/services/RogueTypes'

const rogue = useRogue()
const props = defineProps<{ buyRequest: BuyRequest }>()
const buyRequestRef = toRef(props, 'buyRequest')

const mainRack = computed(() => props.buyRequest.children?.[0])

const {
  sideOptions,
  leftSide,
  rightSide,
  leftSideEnabled,
  rightSideEnabled,
  sideSelection,
  setSideSelection,
} = useSideSelection<'dual' | 'left' | 'right'>(buyRequestRef, { forceDualOnFreshLoad: true })

function handleSideSelectionChange(val: string) {
  setSideSelection(val as 'dual' | 'left' | 'right')
}

const groupMeta = computed(() => getBuyRequestMeta(props.buyRequest, rogue))
// Using a bundle child's meta surfaces leadTime for cave correctly.
const badgeMeta = computed(() => getBuyRequestMeta(leftSide.value ?? props.buyRequest, rogue))

const rackHeight = computed(() => getSimpleForBuyRequest(props.buyRequest, rogue)?.attributes?.rack_height)

provide('rackHeight', rackHeight)

const openAccordions = ref<boolean[]>([true, false, false])

// Keep mainRack qty at 1
watchEffect(() => {
  if (mainRack.value?.qty === 0) mainRack.value.qty = 1
})
</script>

<style lang="scss" scoped>
.container {
  font-size: $size-xs;
  margin-bottom: 10px;

  .divider {
    height: 1px;
    background-color: $color-gray;
    margin: $space-xxs 0 $size-s;
  }
}

.side-checkboxes {
  display: flex;
  gap: $space-m;
  padding-bottom: $space-xs;

  .side-checkbox {
    display: flex;
    align-items: center;
    gap: $space-xs;
    cursor: pointer;
    font-size: $size-xs;

    input[type='checkbox'] {
      cursor: pointer;
      width: $size-s;
      height: $size-s;
    }

    &.disabled {
      opacity: 0.4;
      cursor: not-allowed;

      input {
        cursor: not-allowed;
      }
    }
  }
}
</style>